import { useEffect, useState } from "react";
import { archiveChannel, createChannel, getAdminChannels, updateChannel } from "../api/channels";
import { getCurrentUser } from "../api/users";
import "./AdminChannels.css";

function unwrapChannel(data) {
  return data?.channel || data?.data?.channel || data?.data || data;
}

function unwrapChannels(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.channels)) return data.channels;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.channels)) return data.data.channels;
  return [];
}

function channelId(channel) {
  return channel.id ?? channel.channelId ?? channel.channel_id;
}

function sameChannel(first, second) {
  const firstId = channelId(first);
  const secondId = channelId(second);
  return firstId != null && secondId != null && String(firstId) === String(secondId);
}

function isArchived(channel) {
  return channel.is_archived === true || channel.isArchived === true || channel.archived === true;
}

function normalizeChannel(channel) {
  return {
    ...channel,
    id: channelId(channel),
    name: channel.name ?? channel.channel_name ?? "",
    description: channel.description ?? "",
  };
}

export default function AdminChannels({ token }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [newChannel, setNewChannel] = useState({ name: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let isActive = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const profileResponse = await getCurrentUser(token);
        const profile = profileResponse.data?.user || profileResponse.data?.profile || profileResponse.data;
        if (!isActive) return;
        if (profile?.is_admin !== true) {
          setIsAdmin(false);
          return;
        }

        setIsAdmin(true);
        const response = await getAdminChannels(token);
        if (isActive) setChannels(unwrapChannels(response.data).map(normalizeChannel));
      } catch (requestError) {
        if (isActive) setError(requestError.response?.data?.message || "Unable to load channels.");
      } finally {
        if (isActive) setLoading(false);
      }
    }

    load();
    return () => {
      isActive = false;
    };
  }, [token]);

  async function handleCreate(event) {
    event.preventDefault();
    const values = { name: newChannel.name.trim(), description: newChannel.description.trim() };
    if (!values.name) return;

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const response = await createChannel(token, values);
      const created = normalizeChannel({ ...values, ...unwrapChannel(response.data) });
      setChannels((existing) => [...existing, created]);
      setNewChannel({ name: "", description: "" });
      setNotice(`Channel #${created.name} created.`);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to create this channel.");
    } finally {
      setSaving(false);
    }
  }

  function beginEdit(channel) {
    setError("");
    setNotice("");
    setEditingId(channelId(channel));
    setEditValues({ name: channel.name, description: channel.description });
  }

  async function handleUpdate(event, channel) {
    event.preventDefault();
    const values = { name: editValues.name.trim(), description: editValues.description.trim() };
    if (!values.name) {
      setError("Channel name is required.");
      return;
    }

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const response = await updateChannel(token, channelId(channel), values);
      const updated = unwrapChannel(response.data);
      setChannels((existing) => existing.map((item) =>
        sameChannel(item, channel) ? normalizeChannel({ ...item, ...values, ...(updated || {}) }) : item,
      ));
      setEditingId(null);
      setNotice(`Channel #${values.name} updated.`);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to update this channel.");
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(channel) {
    if (!window.confirm(`Archive #${channel.name}? Messages and memberships will be preserved.`)) return;
    const id = channelId(channel);
    setBusyId(id);
    setError("");
    setNotice("");
    try {
      await archiveChannel(token, id);
      setChannels((existing) => existing.map((item) =>
        sameChannel(item, channel) ? { ...item, is_archived: true } : item,
      ));
      setNotice(`Channel #${channel.name} archived. Messages and memberships are preserved.`);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to archive this channel.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleRestore(channel) {
    const id = channelId(channel);
    setBusyId(id);
    setError("");
    setNotice("");
    try {
      const response = await updateChannel(token, id, { is_archived: false });
      const updated = unwrapChannel(response.data);
      setChannels((existing) => existing.map((item) =>
        sameChannel(item, channel) ? normalizeChannel({ ...item, is_archived: false, ...(updated || {}) }) : item,
      ));
      setNotice(`Channel #${channel.name} restored.`);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to restore this channel.");
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <main className="admin-channels-page"><p className="admin-channels-loading">Checking administrator access...</p></main>;
  }

  if (!isAdmin) {
    return (
      <main className="admin-channels-page">
        <section className="admin-channels-denied" role="alert">
          <h1>Administrator access required</h1>
          <p>Your account cannot manage workspace channels.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-channels-page">
      <header className="admin-channels-heading">
        <div>
          <p className="admin-channels-eyebrow">WORKSPACE ADMIN</p>
          <h1>Channels</h1>
          <p>Public channels, including archived channels.</p>
        </div>
        <span className="admin-channel-count">{channels.length} total</span>
      </header>

      {error && <p className="admin-channels-alert" role="alert">{error}</p>}
      {notice && <p className="admin-channels-notice" role="status">{notice}</p>}

      <form className="admin-channel-create" onSubmit={handleCreate}>
        <div className="admin-channel-create-heading">
          <h2>Create public channel</h2>
          <span>New channels are visible to workspace members.</span>
        </div>
        <div className="admin-channel-create-fields">
          <label>
            <span>Channel name</span>
            <input
              onChange={(event) => setNewChannel((current) => ({ ...current, name: event.target.value }))}
              placeholder="e.g. Design"
              required
              value={newChannel.name}
            />
          </label>
          <label>
            <span>Description</span>
            <input
              onChange={(event) => setNewChannel((current) => ({ ...current, description: event.target.value }))}
              placeholder="What is this channel for?"
              value={newChannel.description}
            />
          </label>
          <button className="admin-channel-primary" disabled={saving} type="submit">
            {saving ? "Creating..." : "Create channel"}
          </button>
        </div>
      </form>

      <div className="admin-channels-list-heading">
        <h2>All channels</h2>
        <span>{channels.filter(isArchived).length} archived</span>
      </div>
      <div className="admin-channels-table-wrap">
        <table className="admin-channels-table">
          <thead>
            <tr>
              <th scope="col">Channel</th>
              <th scope="col">Description</th>
              <th scope="col">Visibility</th>
              <th scope="col">Status</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => {
              const id = channelId(channel);
              const archived = isArchived(channel);
              const isEditing = String(editingId) === String(id);
              return (
                <tr className={archived ? "is-archived" : ""} key={id ?? channel.name}>
                  {isEditing ? (
                    <>
                      <td data-label="Channel">
                        <label className="sr-only" htmlFor={`channel-name-${id}`}>Channel name</label>
                        <input id={`channel-name-${id}`} onChange={(event) => setEditValues((current) => ({ ...current, name: event.target.value }))} required value={editValues.name} />
                      </td>
                      <td data-label="Description">
                        <label className="sr-only" htmlFor={`channel-description-${id}`}>Channel description</label>
                        <input id={`channel-description-${id}`} onChange={(event) => setEditValues((current) => ({ ...current, description: event.target.value }))} value={editValues.description} />
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="admin-channel-name" data-label="Channel"># {channel.name}</td>
                      <td data-label="Description">{channel.description || "—"}</td>
                    </>
                  )}
                  <td data-label="Visibility">{channel.is_public === false || channel.isPublic === false ? "Private" : "Public"}</td>
                  <td data-label="Status"><span className={`admin-channel-status${archived ? " is-archived" : " is-active"}`}>{archived ? "Archived" : "Active"}</span></td>
                  <td className="admin-channel-actions" data-label="Actions">
                    {isEditing ? (
                      <>
                        <button className="admin-channel-primary" disabled={saving} onClick={(event) => handleUpdate(event, channel)} type="button">{saving ? "Saving..." : "Save"}</button>
                        <button className="admin-channel-secondary" onClick={() => setEditingId(null)} type="button">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="admin-channel-secondary" onClick={() => beginEdit(channel)} type="button">Edit</button>
                        {archived ? (
                          <button className="admin-channel-restore" disabled={busyId === id} onClick={() => handleRestore(channel)} type="button">{busyId === id ? "Restoring..." : "Restore"}</button>
                        ) : (
                          <button className="admin-channel-danger" disabled={busyId === id} onClick={() => handleArchive(channel)} type="button">{busyId === id ? "Archiving..." : "Archive"}</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!channels.length && <tr><td className="admin-channels-empty" colSpan="5">No channels found.</td></tr>}
          </tbody>
        </table>
      </div>
    </main>
  );
}